import { IS_OFFLINE, readThrough, request } from "./http";
import { categories } from "@/mocks/categories";
import { locations } from "@/mocks/locations";
import { businesses } from "@/mocks/businesses";
import { subcategories, businessSubcategories } from "@/mocks/subcategories";
import { touristSpots } from "@/mocks/touristSpots";
import { plans } from "@/mocks/plans";
import { reviews } from "@/mocks/reviews";
import { siteStats, whyUsPoints, howItWorks, testimonials, homeFaqs } from "@/mocks/siteContent";
import { siteSettings } from "@/mocks/siteSettings";
import { marqueeMessages, popupAds } from "@/mocks/announcements";
import { parseBusinessPlaceSlug } from "@/utils/slug";
import { districtNameForSlug, isCoveredDistrict, normalizeDistrict } from "@/config/regions";
import type {
  Business,
  BusinessSearchParams,
  BusinessSearchResult,
  BusinessSuggestion,
  Category,
  Location,
  MarqueeMessage,
  Plan,
  PopupAd,
  Review,
  ReviewSubmission,
  SiteSettings,
  TouristSpot,
} from "@/types";

const LOCATION_SLUGS = (locations as Location[]).map((l) => l.slug);

/**
 * Enriches a demo business with the derived fields the real API also returns
 * (subcategory + location slug) so mock and live shapes stay identical.
 */
function enrich(business: Business): Business {
  const sub = subcategories.find((s) => s.id === businessSubcategories[business.id]);
  const loc = locations.find((l) => l.id === business.locationId);
  return {
    ...business,
    subcategoryId: sub?.id,
    subcategorySlug: sub?.slug,
    subcategoryName: sub?.name,
    locationSlug: loc?.slug,
  };
}

const mockBusinesses = (): Business[] => (businesses as Business[]).map(enrich);

/** Resolves a demo business by its plain slug *or* a compound `<slug>-<place>`. */
function resolveMockBusiness(slug: string): Business | undefined {
  const list = mockBusinesses();
  const exact = list.find((b) => b.slug === slug);
  if (exact) return exact;
  const { businessSlug } = parseBusinessPlaceSlug(slug, LOCATION_SLUGS);
  return list.find((b) => b.slug === businessSlug);
}

/** Client-side implementation of the search endpoint (used when offline). */
function mockSearch(params: BusinessSearchParams): BusinessSearchResult {
  const perPage = params.perPage && params.perPage > 0 ? params.perPage : 9;
  const page = params.page && params.page > 0 ? params.page : 1;
  const query = (params.q || "").toLowerCase().trim();

  const category = params.category
    ? categories.find((c) => c.slug === params.category || c.id === params.category)
    : undefined;
  const location = params.location
    ? locations.find((l) => l.slug === params.location || l.id === params.location)
    : undefined;
  const subcategory = params.subcategory
    ? subcategories.find((s) => s.slug === params.subcategory || s.id === params.subcategory)
    : undefined;

  let list = mockBusinesses().filter((b) => b.status === "approved");

  if (query) {
    list = list.filter(
      (b) =>
        b.name.toLowerCase().includes(query) ||
        b.categoryName.toLowerCase().includes(query) ||
        (b.subcategoryName || "").toLowerCase().includes(query) ||
        b.description.toLowerCase().includes(query) ||
        b.tags.some((t) => t.toLowerCase().includes(query)),
    );
  }
  if (category) list = list.filter((b) => b.categoryId === category.id);
  if (subcategory) list = list.filter((b) => b.subcategoryId === subcategory.id);
  if (location) list = list.filter((b) => b.locationId === location.id);
  if (params.district) {
    const districtName = districtNameForSlug(params.district) || params.district;
    const districtLocIds = locations
      .filter((l) => normalizeDistrict(l.district) === normalizeDistrict(districtName))
      .map((l) => l.id);
    list = list.filter((b) => districtLocIds.includes(b.locationId));
  }
  if (params.town) {
    const townLoc = locations.find((l) => l.slug === params.town || l.id === params.town);
    list = list.filter(
      (b) => b.talukSlug === params.town || (townLoc ? b.locationId === townLoc.id : false),
    );
  }
  if (params.minRating && params.minRating > 0) list = list.filter((b) => b.rating >= params.minRating!);
  if (params.openNow) list = list.filter((b) => b.openNow);

  switch (params.sort) {
    case "rating":
      list = [...list].sort((a, b) => b.rating - a.rating || b.reviewCount - a.reviewCount);
      break;
    case "reviews":
      list = [...list].sort((a, b) => b.reviewCount - a.reviewCount || b.rating - a.rating);
      break;
    case "name":
      list = [...list].sort((a, b) => a.name.localeCompare(b.name));
      break;
    case "newest":
      list = [...list].reverse();
      break;
    default:
      list = [...list].sort(
        (a, b) => Number(b.isFeatured) - Number(a.isFeatured) || b.rating - a.rating,
      );
  }

  const total = list.length;
  const pages = Math.max(1, Math.ceil(total / perPage));
  const items = list.slice((page - 1) * perPage, page * perPage);
  return { items, total, page, perPage, pages };
}

function mockSuggest(query: string, limit: number): BusinessSuggestion[] {
  const needle = query.toLowerCase().trim();
  if (!needle) return [];
  return mockBusinesses()
    .filter((b) => b.status === "approved")
    .filter(
      (b) =>
        b.name.toLowerCase().includes(needle) ||
        b.categoryName.toLowerCase().includes(needle) ||
        b.tags.some((t) => t.toLowerCase().includes(needle)),
    )
    .slice(0, limit)
    .map((b) => ({
      name: b.name,
      slug: b.slug,
      categoryName: b.categoryName,
      categorySlug: categories.find((c) => c.id === b.categoryId)?.slug ?? "",
      locationName: b.locationName,
    }));
}

/**
 * Category artwork lookup. The live categories endpoint currently ships each
 * category without an image, so we match it to the bundled photorealistic
 * artwork by slug — every card keeps a visual in both live and preview modes.
 */
const categoryImageBySlug = new Map(
  (categories as Category[])
    .filter((c): c is Category & { image: string } => Boolean(c.image))
    .map((c) => [c.slug, c.image]),
);

export async function getCategories(): Promise<Category[]> {
  const list = await readThrough<Category[]>("/public/categories", categories as Category[]);
  return list.map((c) => (c.image ? c : { ...c, image: categoryImageBySlug.get(c.slug) }));
}

/**
 * Districts/towns the directory covers. Restricted to the four covered
 * districts so anything outside them never appears in the location lists.
 */
export async function getLocations(): Promise<Location[]> {
  const list = await readThrough<Location[]>("/public/locations", locations as Location[]);
  return list.filter((l) => isCoveredDistrict(l.district));
}

export const getBusinesses = (): Promise<Business[]> =>
  readThrough<Business[]>("/public/businesses", mockBusinesses());

export const getFeaturedBusinesses = (): Promise<Business[]> =>
  readThrough<Business[]>(
    "/public/businesses/featured",
    mockBusinesses().filter((b) => b.isFeatured),
  );

/**
 * Tourist spots, limited to the four districts we cover (Mysore, Mandya,
 * Chamarajanagar and Kodagu). Spots in nearby districts are dropped here so
 * both the district chips and the listings stay in scope.
 */
export async function getTouristSpots(): Promise<TouristSpot[]> {
  const list = await readThrough<TouristSpot[]>("/public/tourist-spots", touristSpots as TouristSpot[]);
  return list.filter((s) => isCoveredDistrict(s.district));
}

export const getPlans = (): Promise<Plan[]> => readThrough<Plan[]>("/public/plans", plans as unknown as Plan[]);

export const getReviews = (): Promise<Review[]> => readThrough<Review[]>("/public/reviews", reviews as Review[]);

/**
 * Submits a new review for a listing (§13). In live mode it posts to
 * `/api/public/reviews`; the server decides whether it is published straight
 * away or held for moderation. Falls back to an optimistic local review when
 * the API is not configured (preview).
 */
export function submitReview(input: ReviewSubmission): Promise<Review> {
  const demo: Review = {
    id: `rev-${Date.now()}`,
    businessId: input.businessId,
    businessName: input.businessName ?? "",
    authorName: input.authorName,
    rating: input.rating,
    comment: input.comment,
    date: new Date().toISOString().slice(0, 10),
    status: "pending",
  };

  return readThrough<Review>("/public/reviews", demo, {
    method: "POST",
    body: {
      business: input.businessId,
      name: input.authorName,
      rating: input.rating,
      comment: input.comment,
    },
  });
}

/**
 * Records a click-to-call / WhatsApp / website contact event against a listing
 * (§14 tracking). Fire-and-forget: failures are swallowed so the visitor's tap
 * is never blocked.
 */
export function trackContactEvent(
  businessId: string,
  event: "call" | "whatsapp" | "website" | "profile",
): void {
  if (IS_OFFLINE) return;
  request<{ received: boolean }>("/public/track", {
    method: "POST",
    body: { business: businessId, event },
  }).catch(() => undefined);
}

export const getSiteStats = async (): Promise<{ id: string; value: string; label: string }[]> => {
  const list = await readThrough<{ id: string; value: string; label: string }[]>(
    "/public/site/stats",
    siteStats,
  );
  // Defensive dedupe: a stale duplicate row (from an older, non-idempotent seed)
  // must never render the same card twice. Exact match only, so the distinct
  // 'Districts Covered' / 'Districts covered' labels are both kept.
  const seen = new Set<string>();
  return list.filter((s) => {
    const key = s.label.trim();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

/**
 * Reads a public content block that should still render a sensible default
 * before its backend endpoint exists / while the API is briefly unreachable.
 * Failures fall back to the bundled demo data instead of breaking the page.
 */
async function readWithFallback<T>(path: string, demo: T): Promise<T> {
  if (IS_OFFLINE) return demo;
  try {
    return await request<T>(path);
  } catch {
    return demo;
  }
}

/** Home-page announcement marquee (admin-managed, home page only). */
export const getMarqueeMessages = (): Promise<MarqueeMessage[]> =>
  readWithFallback<MarqueeMessage[]>("/public/marquee", marqueeMessages as MarqueeMessage[]);

/** Live popup ads for the centre-screen lightbox. */
export const getActivePopupAds = (): Promise<PopupAd[]> =>
  readWithFallback<PopupAd[]>("/public/popup-ads", popupAds as unknown as PopupAd[]);

/**
 * Records that a popup was actually shown to a visitor. Fire-and-forget:
 * failures are swallowed so the popup is never blocked.
 */
export function trackPopupImpression(id: string): void {
  if (IS_OFFLINE) return;
  request<{ received: boolean }>(`/public/popup-ads/${id}/impression`, { method: "POST" }).catch(
    () => undefined,
  );
}

/** Public, admin-editable site identity (name, logo, favicon, contact, social). */
export const getSiteSettings = (): Promise<SiteSettings> =>
  readThrough<SiteSettings>("/public/settings", siteSettings as SiteSettings);
export const getWhyUs = () => readThrough("/public/site/why-us", whyUsPoints);
export const getHowItWorks = () => readThrough("/public/site/how-it-works", howItWorks);
export const getTestimonials = () => readThrough("/public/site/testimonials", testimonials);
export const getHomeFaqs = () => readThrough("/public/site/faqs", homeFaqs);

export function getBusinessBySlug(slug: string): Promise<Business | undefined> {
  return readThrough<Business | undefined>(
    `/public/businesses/${slug}`,
    resolveMockBusiness(slug),
  );
}

export const getCategoryBySlug = (slug: string): Promise<Category | undefined> =>
  readThrough<Category | undefined>(
    `/public/categories/${slug}`,
    (categories as Category[]).find((c) => c.slug === slug),
  );

export const getLocationBySlug = (slug: string): Promise<Location | undefined> =>
  readThrough<Location | undefined>(
    `/public/locations/${slug}`,
    (locations as Location[]).find((l) => l.slug === slug),
  );

export const getTouristSpotBySlug = (slug: string): Promise<TouristSpot | undefined> =>
  readThrough<TouristSpot | undefined>(
    `/public/tourist-spots/${slug}`,
    (touristSpots as TouristSpot[]).find((s) => s.slug === slug),
  );

/**
 * Combined search + filtering. Hits `/public/search` in live mode and falls
 * back to the same filtering applied to demo data when offline.
 */
export function searchBusinesses(params: BusinessSearchParams = {}): Promise<BusinessSearchResult> {
  const perPage = params.perPage ?? 9;
  const page = params.page ?? 1;

  const qs = new URLSearchParams();
  if (params.q) qs.set("q", params.q);
  if (params.category) qs.set("category", params.category);
  if (params.subcategory) qs.set("subcategory", params.subcategory);
  if (params.location) qs.set("location", params.location);
  if (params.district) qs.set("district", params.district);
  if (params.town) qs.set("town", params.town);
  if (params.minRating) qs.set("rating", String(params.minRating));
  if (params.openNow) qs.set("open", "1");
  if (params.sort && params.sort !== "relevance") qs.set("sort", params.sort);
  if (page > 1) qs.set("page", String(page));
  qs.set("per_page", String(perPage));

  const query = qs.toString();
  return readThrough<BusinessSearchResult>(
    `/public/search${query ? `?${query}` : ""}`,
    mockSearch({ ...params, page, perPage }),
  );
}

/** Fast typeahead suggestions for the search box. */
export function suggestBusinesses(query: string, limit = 8): Promise<BusinessSuggestion[]> {
  const q = query.trim();
  if (!q) return Promise.resolve([]);
  const qs = new URLSearchParams({ q, limit: String(limit) });
  return readThrough<BusinessSuggestion[]>(`/public/suggest?${qs.toString()}`, mockSuggest(q, limit));
}